import { useMemo } from 'react';
import { useQuery } from 'react-query';
import ResidentService from '../../services/residentService';
import useResidentQuery from './useResidentQuery';
import { buildHouseholdMembers } from './useHouseholdMembersQuery.helpers';

/**
 * React Query replacement for the Redux pair
 * (EditResidentHousehold's getResidentHousehold + ResidentProfile's
 * getOneResident) that ResidentProfile/selectors.js's
 * getHouseholdResidentMembers combines into a household member list.
 *
 * Fetches the household's adult/minor/pet records and the resident's
 * applicant array independently, then reshapes them with the same
 * cross-referencing logic as that selector (see
 * useHouseholdMembersQuery.helpers.js).
 */
const useHouseholdMembersQuery = ({
  organizationId,
  propertyId,
  residentId,
  options = {},
}) => {
  const residentService = useMemo(() => new ResidentService(), []);
  const enabled =
    Boolean(organizationId && propertyId && residentId) &&
    options.enabled !== false;

  const householdQuery = useQuery(
    ['getResidentHousehold', organizationId, propertyId, residentId],
    () =>
      residentService.getResidentHousehold(
        residentId,
        organizationId,
        propertyId,
      ),
    { enabled },
  );

  const residentQuery = useResidentQuery({
    organizationId,
    propertyId,
    residentId,
    options: { enabled },
  });

  const householdMembers = useMemo(() => {
    if (!householdQuery.data || !residentQuery.data) return [];
    return buildHouseholdMembers({
      household: householdQuery.data,
      applicants: [
        ...(residentQuery.data?.applicantInfo?.applicants ?? []),
        // Prior members removed through the legacy Edit Household flow, whose
        // Applicant is soft-deleted and so missing from the list above.
        ...(residentQuery.data?.removedMemberApplicants ?? []),
      ],
      referralType: residentQuery.data?.prospectInfo?.referralType?.name ?? '',
    });
  }, [householdQuery.data, residentQuery.data]);

  return {
    data: householdMembers,
    isLoading: householdQuery.isLoading || residentQuery.isLoading,
    isFetching: householdQuery.isFetching || residentQuery.isFetching,
    isError: householdQuery.isError || residentQuery.isError,
  };
};

export default useHouseholdMembersQuery;
